"use client"
import { useMemo, useRef } from 'react'
import { type ResolvedBranchNode } from '@/lib/branchTypes'
import { buildPrimaryChain } from '../../data/playbookStateUtils'
import { PlaybookBranchNodeMarkdownContent } from '../PlaybookBranchNodeMarkdownContent'
import { PLAYBOOK_BRANCH_FLOWCHART_ID } from '../choicePointAnchor'

// Fully opaque for the first PLAN_A_PREVIEW_FADE_START_VH, then fades to
// PLAN_A_PREVIEW_FADE_HOLD_OPACITY by PLAN_A_PREVIEW_FADE_END_VH and holds.
// Edit these to tune the fade curve / distance of the Plan A text preview.
export const PLAN_A_PREVIEW_FADE_START_VH = 8
export const PLAN_A_PREVIEW_FADE_END_VH = 38
export const PLAN_A_PREVIEW_FADE_HOLD_OPACITY = 0

// DOM anchor so ChoicePointScrollLock can cap the scroll region at this
// element's top + PLAN_A_PREVIEW_FADE_END_VH (where the fade goes invisible).
export const PLAN_A_PREVIEW_ELEMENT_ID = 'plan-a-preview-subtree'

const PLAN_A_PREVIEW_FADE_MASK =
  `linear-gradient(to bottom,` +
  ` black 0,` +
  ` black ${PLAN_A_PREVIEW_FADE_START_VH}vh,` +
  ` rgba(0,0,0,${PLAN_A_PREVIEW_FADE_HOLD_OPACITY}) ${PLAN_A_PREVIEW_FADE_END_VH}vh,` +
  ` rgba(0,0,0,${PLAN_A_PREVIEW_FADE_HOLD_OPACITY}) 100%)`

type PlanAPreviewSubtreeProps = {
  // The plan node currently hovered, or null when nothing is. null collapses
  // the band (animated); a value reveals that plan's subtree. Switching
  // directly between two plans just swaps the content in place — no detour
  // through closed — so a fast mouse never waits on a collapse/re-open.
  hoveredNodeId: string | null
  nodes: Record<string, ResolvedBranchNode>
}

// Open/close duration for the hovered-plan preview. Only the genuine
// open (none → plan) and close (plan → none) transitions animate over this;
// plan-to-plan switches swap content instantly.
export const PLAN_A_PREVIEW_REVEAL_MS = 100

// Renders the recursive primary subtree of the hovered plan as an unclickable
// preview so readers can sample how the page reads without committing. The
// preview only shows while a plan is hovered; it slides + fades open from
// height 0 to PLAN_A_PREVIEW_FADE_END_VH, and the markdown additionally fades
// from opaque to PLAN_A_PREVIEW_FADE_HOLD_OPACITY across that band.
export const PlanAPreviewSubtree = ({ hoveredNodeId, nodes }: PlanAPreviewSubtreeProps) => {
  const revealed = hoveredNodeId !== null

  // Keep the last hovered plan mounted while collapsing so it fades out in
  // place instead of vanishing; on re-open the new plan replaces it.
  const lastHoveredRef = useRef<string | null>(hoveredNodeId)
  if (hoveredNodeId !== null) lastHoveredRef.current = hoveredNodeId
  const displayedNodeId = hoveredNodeId ?? lastHoveredRef.current

  const chain = useMemo(
    () => (displayedNodeId ? buildPrimaryChain(displayedNodeId, nodes) : []),
    [displayedNodeId, nodes],
  )

  return (
    // Outer wrapper always reserves the full preview band so the empty space
    // beneath the plans stays put whether or not a plan is hovered; only the
    // inner clip's height animates, so the content slides into the reserved gap.
    <div id={PLAN_A_PREVIEW_ELEMENT_ID} className="relative" style={{ minHeight: `${PLAN_A_PREVIEW_FADE_END_VH}vh` }}>
      <div
        className="overflow-hidden transition-[height,opacity] ease-out"
        style={{
          height: revealed ? `${PLAN_A_PREVIEW_FADE_END_VH}vh` : '0vh',
          opacity: revealed ? 1 : 0,
          transitionDuration: `${PLAN_A_PREVIEW_REVEAL_MS}ms`,
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none select-none"
          style={{
            maskImage: PLAN_A_PREVIEW_FADE_MASK,
            WebkitMaskImage: PLAN_A_PREVIEW_FADE_MASK,
          }}
        >
          {chain.map((nodeId) => {
            const node = nodes[nodeId]
            if (!node) return null
            return (
              <PlaybookBranchNodeMarkdownContent
                key={nodeId}
                nodeId={nodeId}
                node={node}
              />
            )
          })}
        </div>
      </div>
      {/* <SelectAPlanCallout /> */}
    </div>
  )
}

// Sits centered over the bottom of the preview's visible band (where the fade
// reaches full transparency), prompting the reader to scroll back up and pick a
// plan. The elbow arrow bends up-and-left toward the plan boxes above. The
// parent preview wrapper is pointer-events-none, so this re-enables clicks.
const SelectAPlanCallout = () => {
  const scrollToPlanChoices = () => {
    document
      .getElementById(PLAYBOOK_BRANCH_FLOWCHART_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div
      className="pointer-events-none absolute inset-x-0 flex justify-center"
      style={{ top: `${PLAN_A_PREVIEW_FADE_END_VH - 6}vh` }}
    >
      <button
        type="button"
        onClick={scrollToPlanChoices}
        className="pointer-events-auto flex items-center gap-2 rounded-md border-2 border-black/50 px-4 py-3 font-sans bg-vivid-background text-base font-bold tracking-wide text-black/75 transition-colors hover:bg-black/5 cursor-pointer pr-8"
      >
        <span className="mr-2">
          <ElbowArrowUpLeft size={14} />
          </span>
        SELECT A PLAN
      </button>
    </div>
  )
}

// Up-and-left elbow: the path enters from the button's left, runs up, then bends
// left into an arrowhead, cueing that the plans live up and to the left above.
// `size` is the only knob — it sets the rendered px box; the path lives in a
// fixed 16-unit viewBox that scales to it, so geometry never needs re-tuning.
const ElbowArrowUpLeft = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-black/55"
    aria-hidden
  >
    <polyline points="14,13 5,13 5,4" />
    <polyline points="2,7 5,3 8,7" />
  </svg>
)
